import { useRef, useEffect, useCallback, useMemo, useState } from "react";
import {
    Box,
    Drawer,
    Typography,
    TextField,
    IconButton,
    Chip,
    CircularProgress,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
} from "@mui/material";
import {
    SmartToy as BotIcon,
    Close as CloseIcon,
    Send as SendIcon,
    AddComment as NewChatIcon,
} from "@mui/icons-material";
import { useNavigate, useLocation } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line } from "recharts";
import { RTL } from "../../cache/CacheRtl";
import { colors, typography, shadows, borders } from "../ui/colors";
import { streamChat } from "../../services/AdminChatService";
import { useAuth } from "../../contexts/AuthContext";

const CHART_COLORS = [colors.primary, "#FF6B6B", "#4ECDC4", "#FFE66D", "#95E1D3", "#F38181", "#AA96DA", "#A8D8EA"];

/**
 * Parse [CHART:type]{json_data} blocks from AI response.
 * Returns { text, charts } where charts is an array of { type, data }.
 */
const parseCharts = (content) => {
    const chartRegex = /\[CHART:(bar|pie|line)\]\s*(\[[\s\S]*?\])\s*\[\/CHART\]/g;
    const charts = [];
    let text = content;

    let match;
    while ((match = chartRegex.exec(content)) !== null) {
        try {
            const data = JSON.parse(match[2]);
            charts.push({ type: match[1], data });
        } catch (e) {
            // skip invalid chart data
        }
    }

    text = text.replace(chartRegex, "").trim();
    return { text, charts };
};

/** Render a chart block (RTL-aware — YAxis on right, data reversed for right-to-left reading) */
const ChatChart = ({ type, data }) => {
    if (!data || data.length === 0) return null;
    const keys = Object.keys(data[0]).filter(k => k !== "name" && k !== "label");
    const rtlData = [...data].reverse();

    return (
        <Box sx={{ width: "100%", height: 200, mt: 1.5, mb: 1, direction: "ltr" }}>
            <ResponsiveContainer>
                {type === "pie" ? (
                    <PieChart>
                        <Pie data={data} dataKey={keys[0] || "value"} nameKey="name" cx="50%" cy="50%" outerRadius={70} label>
                            {data.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                        </Pie>
                        <Tooltip />
                    </PieChart>
                ) : type === "line" ? (
                    <LineChart data={rtlData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="name" tick={{ fontSize: 11 }} reversed />
                        <YAxis tick={{ fontSize: 11 }} orientation="right" />
                        <Tooltip />
                        {keys.map((k, i) => <Line key={k} type="monotone" dataKey={k} stroke={CHART_COLORS[i % CHART_COLORS.length]} strokeWidth={2} />)}
                    </LineChart>
                ) : (
                    <BarChart data={rtlData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="name" tick={{ fontSize: 11 }} reversed />
                        <YAxis tick={{ fontSize: 11 }} orientation="right" />
                        <Tooltip />
                        {keys.map((k, i) => <Bar key={k} dataKey={k} fill={CHART_COLORS[i % CHART_COLORS.length]} radius={[4, 4, 0, 0]} />)}
                    </BarChart>
                )}
            </ResponsiveContainer>
        </Box>
    );
};

/**
 * Convert [LINK:text:/app/path] to markdown links [text](/app/path)
 */
const convertLinks = (content) => {
    return content.replace(/\[LINK:([^\]]+?):(\/app\/[^\]]+)\]/g, '[$1]($2)');
};

/** Custom markdown components for MUI-styled rendering (RTL Hebrew)
 *  NOTE: The chat drawer uses <RTL> (stylis-plugin-rtl) which auto-flips CSS.
 *  So we must NOT set direction/textAlign here — they inherit from the Drawer Paper's direction: "rtl".
 */
const markdownComponents = {
    p: ({ children }) => (
        <Typography sx={{ ...typography.body, color: "inherit", lineHeight: 1.7, mb: 0.5 }}>
            {children}
        </Typography>
    ),
    strong: ({ children }) => (
        <Box component="span" sx={{ fontWeight: 600 }}>{children}</Box>
    ),
    em: ({ children }) => (
        <Box component="span" sx={{ fontStyle: "italic" }}>{children}</Box>
    ),
    ul: ({ children }) => (
        <Box component="ul" sx={{ pr: 2.5, pl: 0, my: 0.5, listStylePosition: "inside" }}>{children}</Box>
    ),
    ol: ({ children }) => (
        <Box component="ol" sx={{ pr: 2.5, pl: 0, my: 0.5, listStylePosition: "inside" }}>{children}</Box>
    ),
    li: ({ children }) => (
        <Box component="li" sx={{ ...typography.body, color: "inherit", lineHeight: 1.7, mb: 0.25 }}>{children}</Box>
    ),
    h1: ({ children }) => (
        <Typography variant="h6" sx={{ ...typography.bodySemibold, color: "inherit", mt: 1, mb: 0.5, fontWeight: 700 }}>{children}</Typography>
    ),
    h2: ({ children }) => (
        <Typography sx={{ ...typography.bodySemibold, color: "inherit", mt: 1, mb: 0.5, fontWeight: 700 }}>{children}</Typography>
    ),
    h3: ({ children }) => (
        <Typography sx={{ ...typography.bodySemibold, color: "inherit", mt: 1, mb: 0.5 }}>{children}</Typography>
    ),
    h4: ({ children }) => (
        <Typography sx={{ ...typography.bodySemibold, color: "inherit", fontSize: "0.9rem", mt: 0.5, mb: 0.25 }}>{children}</Typography>
    ),
    code: ({ children }) => (
        <Box component="code" sx={{ bgcolor: "rgba(0,0,0,0.05)", px: 0.5, borderRadius: "4px", fontSize: "0.85em", fontFamily: "monospace" }}>
            {children}
        </Box>
    ),
    table: ({ children }) => (
        <TableContainer sx={{ my: 1, borderRadius: borders.radius.md, border: borders.default, maxWidth: "100%", overflowX: "auto" }}>
            <Table size="small">{children}</Table>
        </TableContainer>
    ),
    thead: ({ children }) => (
        <TableHead sx={{ bgcolor: colors.bg }}>{children}</TableHead>
    ),
    tbody: ({ children }) => (
        <TableBody>{children}</TableBody>
    ),
    tr: ({ children }) => (
        <TableRow sx={{ "&:last-child td": { borderBottom: 0 } }}>{children}</TableRow>
    ),
    th: ({ children }) => (
        <TableCell sx={{ ...typography.small, fontWeight: 600, color: colors.textSecondary, py: 0.75, px: 1, whiteSpace: "nowrap" }}>
            {children}
        </TableCell>
    ),
    td: ({ children }) => (
        <TableCell sx={{ ...typography.small, color: colors.textPrimary, py: 0.75, px: 1 }}>
            {children}
        </TableCell>
    ),
};

const SUGGESTED_QUESTIONS = [
    "מי המתאמנים שלא התאמנו השבוע?",
    "מה ההתפלגות לפי רמת ניסיון?",
    "איך יוצרים תוכנית אימונים?",
    "מי המתאמן הכי פעיל החודש?",
    "כמה צ'ק-אינים ממתינים לטיפול?",
    "הראה לי מגמת משקל של המתאמנים",
];

const AdminChatbot = ({ isOpen, onClose, onOpen }) => {
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [statusText, setStatusText] = useState("");
    const [streamingText, setStreamingText] = useState("");
    const messagesEndRef = useRef(null);
    const abortRef = useRef(null);
    const navigate = useNavigate();
    const location = useLocation();

    // Extend markdown components with link handler that uses navigate
    const mdComponents = useMemo(() => ({
        ...markdownComponents,
        a: ({ href, children }) => {
            if (href && href.startsWith("/app/")) {
                return (
                    <Box
                        component="span"
                        onClick={() => {
                            onClose();
                            setTimeout(() => navigate(href), 300);
                        }}
                        sx={{
                            color: colors.primary,
                            fontWeight: 500,
                            cursor: "pointer",
                            textDecoration: "underline",
                            textDecorationStyle: "dotted",
                            textUnderlineOffset: "3px",
                            "&:hover": { color: colors.primaryDark },
                        }}
                    >
                        {children}
                    </Box>
                );
            }
            return <a href={href} target="_blank" rel="noopener noreferrer">{children}</a>;
        },
    }), [navigate]);

    const scrollToBottom = useCallback(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, []);

    useEffect(() => {
        scrollToBottom();
    }, [messages, streamingText, statusText, scrollToBottom]);

    const handleSend = useCallback((text) => {
        const message = text || input.trim();
        if (!message || isLoading) return;

        setInput("");
        setIsLoading(true);
        setStatusText("");
        setStreamingText("");

        // Add user message
        const userMsg = { role: "user", content: message };
        setMessages(prev => [...prev, userMsg]);

        // Send full conversation history — server handles summarization for long conversations
        const history = [...messages, userMsg]
            .map(m => ({ role: m.role, content: m.content }));

        abortRef.current = streamChat(message, history, location.pathname, {
            onStatus: (status) => {
                setStatusText(status);
            },
            onToken: (token) => {
                setStatusText("");
                setStreamingText(prev => prev + token);
            },
            onComplete: (fullResponse) => {
                setStreamingText("");
                setMessages(prev => [...prev, {
                    role: "assistant",
                    content: fullResponse,
                }]);
                setIsLoading(false);
                setStatusText("");
            },
            onError: (errorMsg) => {
                setStreamingText("");
                setStatusText("");
                setMessages(prev => [...prev, {
                    role: "assistant",
                    content: errorMsg || "אירעה שגיאה, נסה שוב.",
                    isError: true,
                }]);
                setIsLoading(false);
            },
        });
    }, [input, isLoading, messages, location.pathname]);

    const handleKeyDown = (e) => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    };

    const handleClose = () => {
        if (abortRef.current) {
            abortRef.current.abort();
        }
        onClose();
    };

    const handleNewConversation = () => {
        if (abortRef.current) {
            abortRef.current.abort();
        }
        setMessages([]);
        setInput("");
        setIsLoading(false);
        setStatusText("");
        setStreamingText("");
    };

    // Parse AI response: extract routes, charts, convert links, and render markdown
    const renderMessageContent = useCallback((content) => {
        const routeMatch = content.match(/\[ROUTE:(\/app\/[^\]]+)\]/);
        let cleaned = content.replace(/\[ROUTE:\/app\/[^\]]+\]/g, "").trim();

        // Convert [LINK:text:/path] to markdown links
        cleaned = convertLinks(cleaned);

        const { text, charts } = parseCharts(cleaned);

        return (
            <Box>
                <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>
                    {text}
                </ReactMarkdown>
                {charts.map((chart, i) => (
                    <ChatChart key={i} type={chart.type} data={chart.data} />
                ))}
                {routeMatch && (
                    <Chip
                        label="קח אותי לשם"
                        onClick={() => {
                            navigate(routeMatch[1]);
                            onClose();
                        }}
                        size="small"
                        sx={{
                            mt: 1,
                            bgcolor: colors.primary,
                            color: colors.white,
                            fontFamily: typography.fontFamily,
                            cursor: "pointer",
                            "&:hover": { bgcolor: colors.primaryDark },
                        }}
                    />
                )}
            </Box>
        );
    }, [navigate, mdComponents]);

    return (
        <>
            {/* Chat Drawer */}
            <Drawer
                anchor="right"
                open={isOpen}
                onClose={handleClose}
                transitionDuration={0}
                PaperProps={{
                    sx: {
                        width: "450px",
                        maxWidth: "100vw",
                        direction: "rtl",
                        display: "flex",
                        flexDirection: "column",
                    },
                }}
            >
                <RTL>
                    {/* Header */}
                    <Box
                        sx={{
                            padding: "16px 20px",
                            borderBottom: borders.default,
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            bgcolor: colors.white,
                            flexShrink: 0,
                        }}
                    >
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                            <Box
                                sx={{
                                    width: 40,
                                    height: 40,
                                    borderRadius: borders.radius.md,
                                    background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.primaryDark} 100%)`,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                }}
                            >
                                <BotIcon sx={{ color: colors.white, fontSize: 22 }} />
                            </Box>
                            <Box>
                                <Typography sx={{ ...typography.bodySemibold, color: colors.textPrimary }}>
                                    עוזר AI
                                </Typography>
                                <Typography sx={{ ...typography.tiny, color: colors.textMuted }}>
                                    שאל שאלות על המתאמנים שלך
                                </Typography>
                            </Box>
                        </Box>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                            {messages.length > 0 && (
                                <IconButton
                                    onClick={handleNewConversation}
                                    size="small"
                                    title="שיחה חדשה"
                                    sx={{ color: colors.textMuted, "&:hover": { color: colors.primary } }}
                                >
                                    <NewChatIcon fontSize="small" />
                                </IconButton>
                            )}
                            <IconButton onClick={handleClose} size="small" sx={{ color: colors.textMuted }}>
                                <CloseIcon />
                            </IconButton>
                        </Box>
                    </Box>

                    {/* Messages Area */}
                    <Box
                        sx={{
                            flex: 1,
                            overflow: "auto",
                            padding: "16px",
                            display: "flex",
                            flexDirection: "column",
                            gap: 2,
                            bgcolor: colors.bg,
                        }}
                    >
                        {/* Welcome message */}
                        {messages.length === 0 && !isLoading && (
                            <Box sx={{ textAlign: "center", mt: 4 }}>
                                <BotIcon sx={{ fontSize: 48, color: colors.primary, mb: 2, opacity: 0.5 }} />
                                <Typography sx={{ ...typography.body, color: colors.textSecondary, mb: 3 }}>
                                    שלום! אני העוזר החכם שלך.
                                    <br />
                                    אפשר לשאול אותי על נתוני מתאמנים, סטטיסטיקות, מגמות,
                                    <br />
                                    או איך לבצע פעולות בפאנל הניהול.
                                </Typography>

                                {/* Suggested questions */}
                                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, justifyContent: "center" }}>
                                    {SUGGESTED_QUESTIONS.map((q, i) => (
                                        <Chip
                                            key={i}
                                            label={q}
                                            onClick={() => handleSend(q)}
                                            variant="outlined"
                                            size="small"
                                            sx={{
                                                fontFamily: typography.fontFamily,
                                                ...typography.small,
                                                borderColor: colors.border,
                                                color: colors.textSecondary,
                                                cursor: "pointer",
                                                "&:hover": {
                                                    borderColor: colors.primary,
                                                    bgcolor: colors.primarySubtle,
                                                },
                                            }}
                                        />
                                    ))}
                                </Box>
                            </Box>
                        )}

                        {/* Message bubbles */}
                        {messages.map((msg, idx) => (
                            <Box
                                key={idx}
                                sx={{
                                    display: "flex",
                                    justifyContent: msg.role === "user" ? "flex-start" : "flex-end",
                                }}
                            >
                                <Box
                                    sx={{
                                        maxWidth: msg.role === "user" ? "85%" : "95%",
                                        padding: "10px 14px",
                                        borderRadius: borders.radius.lg,
                                        bgcolor: msg.role === "user"
                                            ? colors.primary
                                            : msg.isError
                                                ? colors.errorBg
                                                : colors.white,
                                        color: msg.role === "user" ? colors.white : colors.textPrimary,
                                        boxShadow: msg.role === "user" ? "none" : shadows.xs,
                                        border: msg.role === "user" ? "none" : borders.default,
                                    }}
                                >
                                    {msg.role === "user" ? (
                                        <Typography sx={{ ...typography.body, color: "inherit", whiteSpace: "pre-wrap" }}>
                                            {msg.content}
                                        </Typography>
                                    ) : (
                                        renderMessageContent(msg.content)
                                    )}
                                </Box>
                            </Box>
                        ))}

                        {/* Streaming / Status display */}
                        {isLoading && (
                            <Box
                                sx={{
                                    display: "flex",
                                    justifyContent: "flex-end",
                                }}
                            >
                                <Box
                                    sx={{
                                        maxWidth: "95%",
                                        padding: "10px 14px",
                                        borderRadius: borders.radius.lg,
                                        bgcolor: colors.white,
                                        boxShadow: shadows.xs,
                                        border: borders.default,
                                    }}
                                >
                                    {statusText && !streamingText && (
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                            <CircularProgress size={14} sx={{ color: colors.primary }} />
                                            <Typography sx={{ ...typography.small, color: colors.textMuted }}>
                                                {statusText}
                                            </Typography>
                                        </Box>
                                    )}
                                    {streamingText && (
                                        <Box sx={{ position: "relative" }}>
                                            <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>
                                                {streamingText}
                                            </ReactMarkdown>
                                            <Box
                                                component="span"
                                                sx={{
                                                    display: "inline-block",
                                                    width: 6,
                                                    height: 16,
                                                    bgcolor: colors.primary,
                                                    ml: 0.5,
                                                    animation: "blink 1s infinite",
                                                    verticalAlign: "text-bottom",
                                                    "@keyframes blink": {
                                                        "0%, 50%": { opacity: 1 },
                                                        "51%, 100%": { opacity: 0 },
                                                    },
                                                }}
                                            />
                                        </Box>
                                    )}
                                </Box>
                            </Box>
                        )}

                        <div ref={messagesEndRef} />
                    </Box>

                    {/* Input Area */}
                    <Box
                        sx={{
                            padding: "12px 16px",
                            borderTop: borders.default,
                            bgcolor: colors.white,
                            display: "flex",
                            gap: 1,
                            alignItems: "flex-end",
                            flexShrink: 0,
                        }}
                    >
                        <TextField
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            onKeyDown={handleKeyDown}
                            placeholder="שאל שאלה..."
                            disabled={isLoading}
                            multiline
                            maxRows={3}
                            fullWidth
                            variant="outlined"
                            size="small"
                            sx={{
                                "& .MuiOutlinedInput-root": {
                                    borderRadius: borders.radius.lg,
                                    fontFamily: typography.fontFamily,
                                    ...typography.body,
                                    bgcolor: colors.bg,
                                    "& fieldset": { borderColor: colors.border },
                                    "&:hover fieldset": { borderColor: colors.borderHover },
                                    "&.Mui-focused fieldset": { borderColor: colors.primary },
                                },
                            }}
                        />
                        <IconButton
                            onClick={() => handleSend()}
                            disabled={isLoading || !input.trim()}
                            sx={{
                                bgcolor: colors.primary,
                                color: colors.white,
                                borderRadius: borders.radius.md,
                                width: 38,
                                height: 38,
                                "&:hover": { bgcolor: colors.primaryDark },
                                "&.Mui-disabled": {
                                    bgcolor: colors.border,
                                    color: colors.textMuted,
                                },
                            }}
                        >
                            <SendIcon sx={{ fontSize: 18, transform: "rotate(180deg)" }} />
                        </IconButton>
                    </Box>
                </RTL>
            </Drawer>
        </>
    );
};

/**
 * Temporary kill switch: while true, the launcher still opens a drawer, but it shows only a
 * maintenance notice — no chat UI, so nothing reaches admin/chat/stream. Flip back to false to
 * restore the assistant.
 */
export const AI_ASSISTANT_PAUSED = true;

export const AI_ASSISTANT_PAUSED_MESSAGE = "במקצה שיפורים אחזור בקרוב";

const PausedAdminChatbot = ({ isOpen, onClose }) => (
    <Drawer
        anchor="right"
        open={isOpen}
        onClose={onClose}
        transitionDuration={0}
        PaperProps={{ sx: { width: "450px", maxWidth: "100vw", direction: "rtl" } }}
    >
        <RTL>
            <Box sx={{ display: "flex", justifyContent: "flex-end", padding: "16px 20px" }}>
                <IconButton onClick={onClose} size="small" sx={{ color: colors.textMuted }}>
                    <CloseIcon />
                </IconButton>
            </Box>
            <Box sx={{ textAlign: "center", mt: 6, px: 3 }}>
                <BotIcon sx={{ fontSize: 48, color: colors.primary, mb: 2, opacity: 0.5 }} />
                <Typography sx={{ ...typography.bodySemibold, color: colors.textPrimary, mb: 1 }}>
                    עוזר AI
                </Typography>
                <Typography sx={{ ...typography.body, color: colors.textSecondary }}>
                    {AI_ASSISTANT_PAUSED_MESSAGE}
                </Typography>
            </Box>
        </RTL>
    </Drawer>
);

/**
 * Role gate, wrapped around the component rather than baked into it.
 *
 * The assistant answers from company-scoped data (clients, programs, nutrition), so it has nothing
 * to work with for the company-less personas (SYSTEM_ADMIN, COMPANY_GROUP_ADMIN) — they don't get
 * it. TopNav hides the launcher for the same reason; this makes the panel itself unreachable
 * however it's opened.
 *
 * It lives HERE, not in DashboardLayout: that layout is rendered by a route which useRoutes()
 * resolves above <AuthProvider> in App, so useAuth() at that level is undefined and destructuring
 * it throws. AdminChatbot renders inside <AuthGuard>, where the context is real.
 *
 * A wrapper, not an early return inside AdminChatbot, because that component runs a long list of
 * hooks — bailing out before them would break the rules of hooks.
 */
const GatedAdminChatbot = (props) => {
    // NOTE the `?? {}`: this component can mount before/outside the auth context (App resolves
    // routes with useRoutes() next to <AuthProvider>, and hot-reload can render it with the
    // context still undefined), and destructuring undefined throws and takes the whole page down.
    // An unknown role is treated as "not company-less", i.e. the assistant renders — the same
    // thing every company user sees, and the launcher in TopNav is gated independently.
    const { isSuperAdmin, isCompanyGroupAdmin } = useAuth() ?? {};
    if (isSuperAdmin?.() || isCompanyGroupAdmin?.()) return null;
    if (AI_ASSISTANT_PAUSED) return <PausedAdminChatbot {...props} />;

    return <AdminChatbot {...props} />;
};

export default GatedAdminChatbot;
